import { type FC, memo } from 'react';
import { RiAlertLine, RiCloseLine, RiSubtractLine } from 'react-icons/ri';
import { useFormContext } from 'react-hook-form-latest';
import type { StepConfig } from '../VerticalStepForm.model';
import { useActiveStepId, useStepStatus } from '../VerticalStepFormContext';
import { getStepVariant, stepHasErrors } from '../VerticalStepForm.utils';
import StepHeader from '../StepHeader/StepHeader';
import StepContent from '../StepContent/StepContent';
import styles from './StepSection.module.scss';

interface StepSectionProps {
  config: StepConfig;
  stepNumber: number;
  onAdvance: (stepId: string) => void;
  onSkip: (stepId: string) => void;
  onGoToStep: (stepId: string) => void;
  onValueChange: (stepId: string) => void;
}

const indicatorClassMap: Record<string, string> = {
  error: styles.indicatorError,
  completed: styles.indicatorCompleted,
  in_progress: styles.indicatorActive,
  pending: styles.indicator,
};

const connectorClassMap: Record<string, string> = {
  error: styles.connectorError,
  completed: styles.connectorCompleted,
  in_progress: styles.connectorActive,
  pending: styles.connectorPending,
};

/** Renders a single step: indicator circle, connector line, and card with header + content. */
const StepSection: FC<StepSectionProps> = ({ config, stepNumber, onAdvance, onSkip, onGoToStep, onValueChange }) => {
  const status = useStepStatus(config.id);
  const activeStepId = useActiveStepId();
  const isExpanded = activeStepId === config.id;
  const {
    formState: { errors },
  } = useFormContext();

  const hasErrors = stepHasErrors(config.fields, errors);
  const stepId = config.id;

  return (
    <div
      className={styles.section}
      data-test-component="StepSection"
      data-test-element="container"
      data-testid={`step-section--${stepId}`}
    >
      <div className={styles.indicatorColumn}>
        <div className={indicatorClassMap[getStepVariant(status, hasErrors)]}>
          {hasErrors && <RiCloseLine size={16} />}
          {!hasErrors && status === 'completed' && (
            <span data-testid={`step-header--${stepNumber}__checkmark`}>&#10003;</span>
          )}
          {!hasErrors && status === 'skipped' && <RiSubtractLine size={16} className={styles.skippedIcon} />}
          {!hasErrors && (status === 'pending' || status === 'in_progress') && config.required && (
            <RiAlertLine size={16} className="red" />
          )}
        </div>
        <div className={connectorClassMap[getStepVariant(status, hasErrors)]} />
      </div>
      <div className={styles.card}>
        <StepHeader
          stepNumber={stepNumber}
          stepId={stepId}
          title={config.title}
          status={status}
          required={config.required}
          isExpanded={isExpanded}
          onGoToStep={onGoToStep}
        />
        {isExpanded && (
          <StepContent
            stepId={stepId}
            fields={config.fields}
            description={config.description}
            required={config.required}
            onAdvance={onAdvance}
            onSkip={onSkip}
            onValueChange={onValueChange}
          />
        )}
      </div>
    </div>
  );
};

export default memo(StepSection);
